import React from 'react';

import { Grid, Skeleton, Typography } from '@mui/material';

import { Field } from '@hrlogics/react-components/form';

import { Nullable } from '@clearverify/types';
import { formatMoney } from '@clearverify/utils';

import {
  ActiveInactive,
  ExistingVerificationRequestTotalChargeReason,
  useGetExistingVerificationRequestTotalChargeQuery,
} from 'src/api';
import { SelectField } from 'src/components/finalForm';
import { useConsumerById } from 'src/hooks/consumer';
import { useStripeCustomerPaymentMethods } from 'src/hooks/stripe';
import { useVerificationRequestFormData } from 'src/hooks/verificationRequest';
import { useCurrentUserVerifier } from 'src/hooks/verifier';
import { useCurrentUserVerifierStaff } from 'src/hooks/verifierStaff';
import type { SelectFieldOption } from 'src/types/components';

import { ReverificationSubmitFormFields } from '../types';

type PaymentBlockProps = {
  inboxItemId: string;
  employeeId: Nullable<string>;
  disabled: boolean;
};

export const PaymentBlock: React.FC<PaymentBlockProps> = ({
  inboxItemId,
  employeeId,
  disabled,
}) => {
  const { setIsTotalChargeLoading } = useVerificationRequestFormData();

  const { isCommercialVerifier } = useCurrentUserVerifier();
  const { verifierStaff } = useCurrentUserVerifierStaff();
  const { consumer: employee } = useConsumerById(employeeId);

  const isPaymentDisabled = !isCommercialVerifier || employee?.freeze === ActiveInactive.Active;

  const { data, loading } = useGetExistingVerificationRequestTotalChargeQuery({
    variables: {
      inboxItemId,
      reason: ExistingVerificationRequestTotalChargeReason.Reverification,
    },
    fetchPolicy: 'no-cache',
    skip: isPaymentDisabled,
  });

  React.useEffect(() => {
    setIsTotalChargeLoading(loading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const { totalCharge } = React.useMemo(
    () =>
      data?.getExistingVerificationRequestTotalCharge || {
        totalCharge: 0,
      },
    [data?.getExistingVerificationRequestTotalCharge],
  );

  const { paymentMethods, loadingPaymentMethods, defaultPaymentMethodId } =
    useStripeCustomerPaymentMethods(!isPaymentDisabled ? verifierStaff?.stripeCustomerId : null);

  const paymentMethodOptions = React.useMemo(
    (): SelectFieldOption[] =>
      paymentMethods.map(paymentMethod => ({
        label: `**** ${paymentMethod.card?.last4 || paymentMethod.us_bank_account?.last4}`,
        value: paymentMethod.id,
      })),
    [paymentMethods],
  );

  if (isPaymentDisabled) {
    return null;
  }

  return (
    <Grid container spacing={2} mt={2} alignItems="center">
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <Typography variant="body2" fontSize={20} fontWeight={500}>
          {loading ? (
            <React.Fragment>Loading...</React.Fragment>
          ) : (
            <React.Fragment>Total charge: {formatMoney(totalCharge)}</React.Fragment>
          )}
        </Typography>
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        {loadingPaymentMethods ? (
          <Skeleton width="100%" height={60} />
        ) : (
          <Field
            name={ReverificationSubmitFormFields.paymentMethodId}
            component={SelectField}
            label="Payment method"
            options={paymentMethodOptions}
            fullWidth
            disabled={disabled}
            required
            initialValue={defaultPaymentMethodId}
          />
        )}
      </Grid>
    </Grid>
  );
};
